import { Box, CircularProgress, Typography } from '@mui/material';
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import BaseCard from '../../../components/Cards/Card';
import CustomEmailInput from '../../../components/EmailInput';
import CustomPasswordInput from "../../../components/PasswordInput";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useFormik } from 'formik';
import { toast, Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useMemo } from 'react';
import { BASE_URL } from '../../../components/BaseUrl';
import { useDispatch } from 'react-redux';
import { setCredentials, setOtpData } from '../../../Redux/Slices/AuthSlice';
import { useEffect } from "react";
import { apiPost } from '../../../utils/apiService';

const Registeration = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasShownToast, setHasShownToast] = useState(false);

const [invitedId, setInvitedId] = useState("");
useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const id = params.get("invited-id");

  if (id) {
    setInvitedId(id);
    localStorage.setItem("invited-id", id);
  }
}, []);
  const validate = (values) => {
    const errors = {};
    if (!values.email) {
      errors.email = t("register.errors.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = t("register.errors.emailInvalid");
    }

    if (!values.password) {
      errors.password = t("register.errors.passwordRequired");
    } else if (values.password.length < 8) {
      errors.password = t("register.errors.passwordShort");
    } else if (
      !/(?=.*[a-z])/.test(values.password) ||
      !/(?=.*[A-Z])/.test(values.password) ||
       !/(?=.*[!@#$%^&*()[\]{}\-_+=~`|:;"'<>,.?/\\])/.test(values.password) 
    ) {
      errors.password = t("register.errors.passwordInvalid");
    }
if (!values.confirmPassword) {
  errors.confirmPassword = t("register.errors.confirmPasswordRequired");
} else if (values.password !== values.confirmPassword) {
  errors.confirmPassword = t("register.errors.passwordMismatch");
}
    return errors;
  };

  const formik = useFormik({
    initialValues: { email: '', password: '',confirmPassword: '' },
    validate,
    onSubmit: async (values) => {
      const errors = validate(values);
      if (Object.keys(errors).length > 0) {
        if (errors.email) toast.error(errors.email);
        else if (errors.password) toast.error(errors.password);
        else if (errors.confirmPassword) toast.error(errors.confirmPassword);
        return;
      }

      setIsSubmitting(true);

      try {
        const data = await apiPost(`${BASE_URL}/company-admins/register`, {
          name: values.email.split('@')[0],
          email: values.email,
          password: values.password,
          confirmPassword: values.password,
          role: "company_admin"
        });

        console.log("Registration API response:", data);

        if (!data.ok || data.error) {
          throw new Error(data.message || "Registration failed");
        }
        const token = data.data.token;

        dispatch(setCredentials({
          token: token,
          user: data.data.user,
        }));

        dispatch(setOtpData({
          otp: data.data.otp,
          userId: data.data.user.id
        }));

        setTimeout(() => navigate("/otp-screen"), 1000);

      } catch (err) {
        toast.error(err.message || "Something went wrong");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

const RuleRow = ({ ok, label, showError }) => {
  const textColor = ok
    ? '#16a34a'   // green if rule passed
    : showError
      ? '#d32f2f' // red if failed AND user clicked continue
      : '#64748b';// gray by default

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: ok ? 'none' : '2px solid #9aa4b2',
          background: ok ? '#22c55e' : 'transparent'
        }}
      >
        {ok ? (
          <span
            style={{
              width: 6,
              height: 10,
              borderRight: '2px solid #fff',
              borderBottom: '2px solid #fff',
              transform: 'rotate(45deg)',
            }}
          />
        ) : (
          <span style={{ position: 'relative', width: 10, height: 10 }}>
            <span
              style={{
                position: 'absolute',
                width: '100%',
                height: 2,
                background: showError ? '#d32f2f' : '#9aa4b2',
                transform: 'rotate(45deg)',
                top: '50%',
                left: 0,
                marginTop: -1,
              }}
            />
            <span
              style={{
                position: 'absolute',
                width: '100%',
                height: 2,
                background: showError ? '#d32f2f' : '#9aa4b2',
                transform: 'rotate(-45deg)',
                top: '50%',
                left: 0,
                marginTop: -1,
              }}
            />
          </span>
        )}
      </span>
      <span style={{ fontSize: 14, color: textColor }}>{label}</span>
    </Box>
  );
};
const PasswordChecklist = ({ password, showError }) => {
  const rules = useMemo(() => ({
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    // special: /[!@#$%^&*()[\]{}\-_+=~`|:;"'<>,.?/\\]/.test(password),
    special: /[!@#$%^&*()[\]{}\-_+=~`|:;"'<>,.?/\\]/.test(password),
  }), [password]);

  return (
<Box sx={{ mt: 1, mb: { xs: 2, sm: 1.5 } }}>
  <RuleRow ok={rules.length} label={t('passwordRules.minLength')} showError={showError && !rules.length}/>
  <RuleRow ok={rules.upper} label={t('passwordRules.upperCase')} showError={showError && !rules.upper}/>
  <RuleRow ok={rules.lower} label={t('passwordRules.lowerCase')} showError={showError && !rules.lower}/>
  <RuleRow ok={rules.number} label={t('passwordRules.number')} showError={showError && !rules.number}/>
  <RuleRow ok={rules.special} label={t('passwordRules.special')} showError={showError && !rules.special}/>
</Box>
  );
};
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  return (
    <Box sx={{ backgroundColor: "#EFF4F8", pt: 0 }}>
      <Toaster />
      <Box sx={{ pt: isMobile ? 5 : 10, pb: isMobile ? 5 : 20, px: 2, display: 'flex', justifyContent: 'center' }}>
        <BaseCard maxWidth={isMobile ? '100%' : 450} sx={{ width: isMobile ? '100%' : 'auto', px: isMobile ? 2 : 0 }}>
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8, mb: 2 }}>
                <CustomText sx={{ ...textStyles.h4, color: theme.palette.tertiary.main, letterSpacing: "0px !important" }}>
                  {t("register.title")}
                </CustomText>
                <CustomText sx={{ ...textStyles.body4, color: theme.palette.text.primary, letterSpacing: "0px !important" }}>
                  {t("register.subtitle")}
                </CustomText>
              </Box>
              <Box >
{invitedId && (
  <Box sx={{ display: "flex", flexDirection: "column", mt: 1 }}>
    <label style={{ marginBottom: "5px" }}>
      Referral Code
    </label>

    <input
      type="text"
      value={invitedId}
      readOnly
      className='project_input'
      style={{
height:"42px",
width:"100%"
      }}
    />
  </Box>
)}
</Box>
              <label style={{ marginTop: "5px" }}>{t("register.emailLabel")}</label>
              <CustomEmailInput
                name="email"
                placeholder="john@gmail.com"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && formik.errors.email}
              />
              {formik.touched.email && formik.errors.email && (
                <span style={{ color: 'red', fontSize: '12px' }}>{formik.errors.email}</span>
              )}

              <label style={{ marginTop: "5px" }}>{t("register.passwordLabel")}</label>
              <CustomPasswordInput
                name="password"
                placeholder="********"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && formik.errors.password}
              />
              {formik.touched.password && formik.errors.password && (
                <span style={{ color: 'red', fontSize: '12px' }}>{formik.errors.password}</span>
              )}

              {/* Add the PasswordChecklist below the password field */}
        



<label style={{ marginTop: "5px" }}>{t("register.confirmPasswordLabel")}</label>
<CustomPasswordInput
  name="confirmPassword"
  placeholder="********"
  value={formik.values.confirmPassword}
  onChange={formik.handleChange}
  onBlur={formik.handleBlur}
  error={formik.touched.confirmPassword && formik.errors.confirmPassword}
/>
{formik.touched.confirmPassword && formik.errors.confirmPassword && (
  <span style={{ color: 'red', fontSize: '12px' }}>{formik.errors.confirmPassword}</span>
)}


      <PasswordChecklist password={formik.values.password} showError={formik.touched.password} />
  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0, mb: 1 }}>
                <input
                  type="checkbox"
                  id="acceptTerms"
                  checked={acceptedTerms}
                  onChange={e => setAcceptedTerms(e.target.checked)}
                  style={{ marginRight: 8, width: 18, height: 18 }}
                  required
                />
                <span
  style={{
 color: "#1976D2",
    cursor: "pointer",
    textDecoration: "underline",
    fontSize: 14,
  }}
  onClick={() => window.open("https://biometricpro.app/terms", "_blank")}
>
  {t("termsAndConditionsText") || "I accept the Terms and Conditions"}
</span>

              </Box>
              <CustomButton
                label={
                  isSubmitting ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={18} sx={{ color: "#fff" }} />
                      <span>{t("register.continue")}</span>
                    </Box>
                  ) : (
                    t("register.continue")
                  )
                }
                type="submit"
                fullWidth
                sx={{ mt: 2 }}
                                disabled={!acceptedTerms || isSubmitting}

              />
            </Box>
          </form>
        </BaseCard>
      </Box>
    </Box>
  );
};

export default Registeration;

















